import React, { useState, useEffect, useCallback } from 'react';
import { ImageIcon } from 'lucide-react';
import Button from '../../components/Button';
import GlassPanel from '../../components/GlassPanel';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { categoryApi } from '../../services/categoryApi';
import { questionApi } from '../../services/questionApi';
import { Category } from '../../types/category';
import { Question, QuestionType } from '../../types/question';
import { useTheme } from '../../context/ThemeContext';
import { getUserRole } from '../../utils/auth';

export interface OperationResult {
  status: number;
  message: string;
}

interface AddQuestionProps {
  onQuestionAdded: () => void;
}

const AddQuestion: React.FC<AddQuestionProps> = ({ onQuestionAdded }) => {
  const { theme } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaintainer, setIsMaintainer] = useState(false);
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [newQuestion, setNewQuestion] = useState<Omit<Question, 'id'>>({
    category: '',
    question: '',
    answer: '',
    difficulty: 'MEDIUM',
    questionType: QuestionType.FREE,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [operationResult, setOperationResult] = useState<OperationResult | null>(null);
  const [questionFilePreview, setQuestionFilePreview] = useState<string>('');
  const [answerFilePreview, setAnswerFilePreview] = useState<string>('');
  const [selectedQuestionFile, setSelectedQuestionFile] = useState<File | null>(null);
  const [selectedAnswerFile, setSelectedAnswerFile] = useState<File | null>(null);

  // Check if user is admin or maintainer
  useEffect(() => {
    const { isAdmin: admin, isMaintainer: maintainer } = getUserRole();
    setIsAdmin(admin);
    setIsMaintainer(maintainer);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryApi.getAllCategories(0, 100);
        setCategories(response.content);
      } catch (error: any) {
        setOperationResult({ status: error.response?.status || 500, message: 'فشل في جلب الفئات' });
      }
    };
    fetchCategories();
    return () => {
      if (questionFilePreview) URL.revokeObjectURL(questionFilePreview);
      if (answerFilePreview) URL.revokeObjectURL(answerFilePreview);
    };
  }, [questionFilePreview, answerFilePreview]);

  const { getRootProps: getQuestionRootProps, getInputProps: getQuestionInputProps, isDragActive: isQuestionDragActive } = useDropzone({
    onDrop: useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 500000) {
        setSelectedQuestionFile(file);
        setQuestionFilePreview(URL.createObjectURL(file));
      }
    }, []),
    accept: { 'image/*': [] },
    maxSize: 500000,
    multiple: false,
  });

  const { getRootProps: getAnswerRootProps, getInputProps: getAnswerInputProps, isDragActive: isAnswerDragActive } = useDropzone({
    onDrop: useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 500000) {
        setSelectedAnswerFile(file);
        setAnswerFilePreview(URL.createObjectURL(file));
      }
    }, []),
    accept: { 'image/*': [] },
    maxSize: 500000,
    multiple: false,
  });

  const handleSubmitQuestion = useCallback(async () => {
    if (!isAdmin && !isMaintainer) {
      setOperationResult({ status: 403, message: 'ليس لديك صلاحية لإضافة أسئلة' });
      return;
    }
    
    if (!newQuestion.category || !newQuestion.question) {
      setOperationResult({ status: 400, message: 'الفئة ونص السؤال مطلوبان' });
      return;
    }

    try {
      setIsLoading(true);
      const createdQuestion = await questionApi.createQuestion(newQuestion as Question);
      const questionId = (createdQuestion as any).id || (createdQuestion as any).questionId;
      if (!questionId) throw new Error('No question ID returned from creation');

      if (selectedQuestionFile || selectedAnswerFile) {
        await questionApi.uploadQuestionPhotos(
          questionId,
          selectedQuestionFile || new File([], 'empty'),
          selectedAnswerFile || new File([], 'empty')
        );
      }
      setOperationResult({ status: 200, message: 'تم إنشاء السؤال بنجاح' });
      setNewQuestion({ 
        category: newQuestion.category, 
        question: '', 
        answer: '', 
        difficulty: 'MEDIUM',
        questionType: newQuestion.questionType
      });
      setSelectedQuestionFile(null);
      setSelectedAnswerFile(null);
      setQuestionFilePreview('');
      setAnswerFilePreview('');
      onQuestionAdded();
    } catch (error: any) {
      setOperationResult({
        status: error.response?.status || 500,
        message: 'فشل في إنشاء السؤال: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setIsLoading(false);
    }
  }, [newQuestion, selectedQuestionFile, selectedAnswerFile, onQuestionAdded, isAdmin, isMaintainer]);

  const bgColor = theme === 'light' ? 'bg-[#F7F9FB]/80' : 'bg-gray-800/90';
  const textColor = theme === 'light' ? 'text-[#1A202C]' : 'text-gray-100';
  const borderColor = theme === 'light' ? 'border-[#E8ECEF]' : 'border-gray-700';
  const inputBgColor = theme === 'light' ? 'bg-white' : 'bg-gray-700';
  const inputBorderColor = theme === 'light' ? 'border-[#E8ECEF]' : 'border-gray-600';
  const accentColor = theme === 'light' ? '[#4B7BFF]' : 'blue-400';
  const iconColor = theme === 'light' ? 'text-[#4A5568]' : 'text-gray-400';
  
  const getDragBorderColor = (isActive: boolean) => {
    if (isActive) {
      return theme === 'light' ? 'border-[#4B7BFF] bg-[#4B7BFF]/10' : 'border-blue-500 bg-blue-900/20';
    }
    return theme === 'light' ? 'border-[#E8ECEF] hover:border-[#4B7BFF]/50' : 'border-gray-700 hover:border-blue-500/50';
  };

  // Display message if user is not an admin or maintainer
  if (!isAdmin && !isMaintainer) {
    return (
      <div className="max-w-2xl mx-auto">
        <GlassPanel className={`p-6 ${bgColor} backdrop-blur-sm shadow-lg rounded-lg border ${borderColor}`}>
          <div className={`p-3 rounded-lg text-center ${
            theme === 'light' 
              ? 'bg-red-50 text-red-800 border border-red-200' 
              : 'bg-red-900/30 text-red-300 border border-red-800'
          }`}>
            <p className="font-medium">ليس لديك صلاحية لإضافة أسئلة. هذه الوظيفة متاحة فقط للمشرفين ومسؤولي الصيانة.</p>
          </div>
        </GlassPanel>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <GlassPanel className={`p-6 ${bgColor} backdrop-blur-sm shadow-lg rounded-lg border ${borderColor}`}>
        <h2 className={`text-xl font-bold ${textColor} mb-6 text-center`}>إضافة سؤال جديد</h2>
        
        {operationResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-3 rounded-lg mb-6 text-center ${
              operationResult.status === 200 
                ? theme === 'light' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-green-900/30 text-green-300 border border-green-800'
                : theme === 'light'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-red-900/30 text-red-300 border border-red-800'
            }`}
          >
            <p className="font-medium text-sm">{operationResult.message}</p>
          </motion.div>
        )}

        <div className="space-y-6">
          <div>
            <label className={`block text-sm font-medium ${textColor} mb-1`}>الفئة</label>
            <select
              value={newQuestion.category}
              onChange={(e) => setNewQuestion({ ...newQuestion, category: e.target.value })}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} focus:outline-none focus:ring-2 focus:ring-${accentColor} focus:border-transparent`}
            >
              <option value="">اختر فئة</option>
              {categories
                .filter(category => category.categoryId)
                .map(category => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.categoryName}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-1`}>نص السؤال</label>
            <textarea
              placeholder="أدخل نص السؤال هنا"
              value={newQuestion.question}
              onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} focus:outline-none focus:ring-2 focus:ring-${accentColor} focus:border-transparent resize-none`}
              rows={4}
            />
          </div>

          <div
            {...getQuestionRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${getDragBorderColor(isQuestionDragActive)}`}
          >
            <input {...getQuestionInputProps()} />
            {questionFilePreview ? (
              <div className="relative aspect-video rounded-lg overflow-hidden">
                <img src={questionFilePreview} alt="معاينة السؤال" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-2">
                <ImageIcon className={`w-10 h-10 ${iconColor} mb-2`} />
                <p className={`text-sm ${iconColor} text-center`}>
                  اسحب صورة السؤال أو انقر للتحميل (اختياري، حد أقصى 500 كيلو بايت)
                </p>
              </div>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-1`}>الإجابة</label>
            <textarea
              placeholder="أدخل الإجابة هنا"
              value={newQuestion.answer}
              onChange={(e) => setNewQuestion({ ...newQuestion, answer: e.target.value })}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} focus:outline-none focus:ring-2 focus:ring-${accentColor} focus:border-transparent resize-none`}
              rows={4}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-1`}>نطاق الخطأ المسموح به</label>
            <input
              type="number"
              placeholder="أدخل نطاق الخطأ المسموح به (اختياري)"
              value={newQuestion.answerRange || ''}
              onChange={(e) => setNewQuestion({ ...newQuestion, answerRange: e.target.value ? Number(e.target.value) : undefined })}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor} focus:border-transparent
                         [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              min="0"
              step="0.1"
            />
            <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
              استخدم هذا الحقل فقط إذا كانت الإجابة رقماً وتريد السماح بنطاق خطأ
            </p>
          </div>

          <div
            {...getAnswerRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${getDragBorderColor(isAnswerDragActive)}`}
          >
            <input {...getAnswerInputProps()} />
            {answerFilePreview ? (
              <div className="relative aspect-video rounded-lg overflow-hidden">
                <img src={answerFilePreview} alt="معاينة الإجابة" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-2">
                <ImageIcon className={`w-10 h-10 ${iconColor} mb-2`} />
                <p className={`text-sm ${iconColor} text-center`}>
                  اسحب صورة الإجابة أو انقر للتحميل (اختياري، حد أقصى 500 كيلو بايت)
                </p>
              </div>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>مستوى الصعوبة</label>
            <div className="flex justify-center gap-6">
              {(['EASY', 'MEDIUM', 'HARD'] as const).map(diff => (
                <label key={diff} className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    value={diff}
                    checked={newQuestion.difficulty === diff}
                    onChange={() => setNewQuestion({ ...newQuestion, difficulty: diff })}
                    className={`form-radio text-${accentColor} focus:ring-${accentColor}`}
                  />
                  <span className={`text-sm font-medium ml-2 ${
                    diff === 'EASY' 
                      ? theme === 'light' ? 'text-green-600' : 'text-green-400'
                      : diff === 'MEDIUM' 
                      ? theme === 'light' ? 'text-yellow-600' : 'text-yellow-400' 
                      : theme === 'light' ? 'text-red-600' : 'text-red-400'
                  }`}>
                    {diff === 'EASY' ? 'سهل' : diff === 'MEDIUM' ? 'متوسط' : 'صعب'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>نوع السؤال</label>
            <div className="flex justify-center gap-6">
              {([QuestionType.FREE, QuestionType.PAYED] as const).map(type => (
                <label key={type} className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    value={type}
                    checked={newQuestion.questionType === type}
                    onChange={() => setNewQuestion({ ...newQuestion, questionType: type })}
                    className={`form-radio text-${accentColor} focus:ring-${accentColor}`}
                  />
                  <span className={`text-sm font-medium ml-2 ${
                    type === QuestionType.FREE 
                      ? theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                      : theme === 'light' ? 'text-purple-600' : 'text-purple-400'
                  }`}>
                    {type === QuestionType.FREE ? 'مجاني' : 'مدفوع'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <Button
            variant="primary"
            onClick={handleSubmitQuestion}
            disabled={isLoading || !newQuestion.category || !newQuestion.question}
            className={`w-full py-3 text-sm mt-6 ${
              theme === 'light' ? 'bg-[#4B7BFF] hover:bg-[#3D63CC]' : 'bg-blue-500 hover:bg-blue-600'
            } text-white disabled:bg-gray-400 ${theme === 'dark' ? 'disabled:bg-gray-600' : ''}`}
          >
            {isLoading ? 'جارٍ الإنشاء...' : 'إنشاء سؤال'}
          </Button>
        </div>
      </GlassPanel>
    </div>
  );
};

export default AddQuestion;